import { useCallback, useEffect, useState } from "react";

import type { TaskDraft } from "../createSeasonData";

/**
 * Cross-season task clipboard, backed by `sessionStorage`.
 *
 * The copy/paste editor lives inside `ChallengesAndTasks`, which mounts once per
 * season. An in-memory clipboard would therefore be wiped the moment the admin
 * navigates to a different season. Persisting the copied task in
 * `sessionStorage` lets a task copied in one season be pasted into a challenge
 * in ANY other season opened in the same browser tab — the paste always creates
 * the task under the *target* card's `season_id` + `challenge_id`, so the copy
 * just needs to survive the navigation.
 *
 * Scope is deliberately the tab/session (not `localStorage`): the clipboard is
 * a transient convenience, not durable state, and shouldn't bleed across tabs
 * or persist after the tab closes.
 */
export const TASK_CLIPBOARD_STORAGE_KEY = "gamePass.taskClipboard";

/**
 * Dispatched on `window` after the clipboard changes so every mounted editor
 * re-reads it. The native `storage` event only fires in OTHER tabs, never the
 * one that made the write, so we need our own same-tab signal to keep multiple
 * mounted consumers in sync.
 */
export const TASK_CLIPBOARD_EVENT = "gamePass:taskClipboardChange";

/**
 * Strip the non-serializable `imageFile` (a `File`) before persisting. A copied
 * task references its image by `imgKey` (the S3 object key) and `imageUrl` (the
 * BE preview URL), both of which survive a JSON round-trip and let the paste
 * re-reference the exact same S3 object under the new season/challenge — no
 * re-upload, no broken preview. In practice every task that can be copied is
 * already persisted (its image lives at `imgKey`), so dropping the transient
 * `File` loses nothing.
 */
const toStorableTask = (task: TaskDraft): Omit<TaskDraft, "imageFile"> => {
  const { imageFile: _imageFile, ...rest } = task;
  return rest;
};

/** Read the copied task, or `null` when the clipboard is empty / unreadable. */
export const readTaskClipboard = (): TaskDraft | null => {
  try {
    const raw = sessionStorage.getItem(TASK_CLIPBOARD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TaskDraft;
    // Defensive: a hand-edited / corrupted payload must not crash the editor.
    // Anything missing the minimal task shape is treated as an empty clipboard.
    if (!parsed || typeof parsed !== "object" || !parsed.trigger) return null;
    // `imageFile` can't round-trip through JSON; normalise it back to undefined
    // so consumers reuse `imgKey` / `imageUrl` instead of a dead `File`.
    return { ...parsed, imageFile: undefined };
  } catch {
    return null;
  }
};

/** Persist `task` to the clipboard and notify same-tab consumers. */
export const writeTaskClipboard = (task: TaskDraft): void => {
  try {
    sessionStorage.setItem(
      TASK_CLIPBOARD_STORAGE_KEY,
      JSON.stringify(toStorableTask(task)),
    );
    window.dispatchEvent(new Event(TASK_CLIPBOARD_EVENT));
  } catch {
    // `sessionStorage` can throw (private mode, quota). Copy/paste is a
    // convenience, so fail silently rather than break the editor.
  }
};

/** Empty the clipboard and notify same-tab consumers. */
export const clearTaskClipboard = (): void => {
  try {
    sessionStorage.removeItem(TASK_CLIPBOARD_STORAGE_KEY);
    window.dispatchEvent(new Event(TASK_CLIPBOARD_EVENT));
  } catch {
    // no-op — see writeTaskClipboard.
  }
};

export type UseTaskClipboardResult = {
  /** The task currently on the clipboard, or `null` when empty. */
  copiedTask: TaskDraft | null;
  /** Copy a task to the clipboard (persisted across navigation). */
  copyTask: (task: TaskDraft) => void;
  /** Clear the clipboard. */
  clearClipboard: () => void;
};

/**
 * Reactive view over the `sessionStorage` task clipboard. Seeds from storage on
 * mount (so a task copied before navigating to this season is immediately
 * paste-ready) and stays in sync via the same-tab `TASK_CLIPBOARD_EVENT` and the
 * cross-tab `storage` event.
 */
export const useTaskClipboard = (): UseTaskClipboardResult => {
  const [copiedTask, setCopiedTask] = useState<TaskDraft | null>(() =>
    readTaskClipboard(),
  );

  useEffect(() => {
    const sync = () => setCopiedTask(readTaskClipboard());
    window.addEventListener(TASK_CLIPBOARD_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(TASK_CLIPBOARD_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const copyTask = useCallback((task: TaskDraft) => {
    writeTaskClipboard(task);
    // Reflect the stored (image-file-stripped) shape immediately so the editor
    // that made the copy doesn't hold a `File` the persisted copy won't have.
    setCopiedTask(readTaskClipboard());
  }, []);

  const clearClipboard = useCallback(() => {
    clearTaskClipboard();
    setCopiedTask(null);
  }, []);

  return { copiedTask, copyTask, clearClipboard };
};
